import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslatePipe } from 'app/core/i18n/translate.pipe';
import { BusinessPartnerService } from 'app/modules/components/business-partners/business-partner/business-partner.service';
import { CalendarPeriodicityComponent } from 'app/modules/components/routing/sub-routes/sub-route-periodicity/calendar-periodicity/calendar-periodicity.component';
import { debounceTime, distinctUntilChanged, Subject } from 'rxjs';
import { BusinessPartnerBySubRoute } from '../../../interfaces/busines-partner-by-sub-route.interface';
import { AssignedPartnersSubRouteFilters } from '../../sub-route-partners.types';
import { BPGroupedItem, BPGroupingCriteria } from '../../types/bp-grouping.types';
import { PartnerCardComponent } from '../partner-card/partner-card.component';

export interface GroupPartnersModalData {
  group: BPGroupedItem;
  subRouteId: string;
  groupingCriteria: BPGroupingCriteria;
  /** Route company Mongo `_id` — scopes the partner list to the sub-route's company. */
  companyId?: string | null;
}

@Component({
  selector: 'app-group-partners-modal',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatPaginatorModule,
    MatTooltipModule,
    PartnerCardComponent,
    TranslatePipe,
  ],
  template: `
    <!-- Header -->
    <div class="flex items-center justify-between border-b p-4">
      <div class="flex items-center gap-2">
        <h2 class="text-base font-bold">{{ data.group.name }}</h2>
        <span class="count-badge">{{ totalItems() }}</span>
      </div>
      <button mat-icon-button (click)="onClose()">
        <mat-icon>close</mat-icon>
      </button>
    </div>

    <!-- Search -->
    <div class="modal-search">
      <mat-form-field class="w-full" subscriptSizing="dynamic">
        <mat-icon matPrefix class="mr-2">search</mat-icon>
        <input
          matInput
          type="text"
          [placeholder]="'routing.SUB_ROUTE_PARTNERS.SIDEBAR.SEARCH_PLACEHOLDER' | t"
          [ngModel]="searchTerm()"
          (ngModelChange)="onSearchChange($event)"
        />
        @if (searchTerm()) {
          <button matSuffix mat-icon-button (click)="onSearchChange('')">
            <mat-icon>close</mat-icon>
          </button>
        }
      </mat-form-field>
    </div>

    <!-- Selection Bar -->
    @if (selectedIds().size > 0) {
      <div class="selection-bar">
        <span class="text-sm font-medium">
          {{ 'routing.SUB_ROUTE_PARTNERS.SIDEBAR.SELECTED_COUNT' | t: { count: selectedIds().size } }}
        </span>
        <div class="flex items-center gap-1">
          <button
            mat-icon-button
            color="primary"
            (click)="openBulkCalendarModal()"
            [matTooltip]="'routing.SUB_ROUTE_PARTNERS.SIDEBAR.ASSIGN_FREQ_SELECTED_TOOLTIP' | t"
          >
            <mat-icon svgIcon="heroicons_outline:calendar-days"></mat-icon>
          </button>
          <button mat-icon-button (click)="clearSelection()" [matTooltip]="'routing.SUB_ROUTE_PARTNERS.SIDEBAR.CLEAR_SELECTION_TOOLTIP' | t">
            <mat-icon>close</mat-icon>
          </button>
        </div>
      </div>
    }

    <!-- Content -->
    <mat-dialog-content class="!p-0">
      @if (loading()) {
        <div class="flex items-center justify-center py-12">
          <mat-spinner diameter="32" />
          <span class="text-secondary ml-2 text-sm">{{ 'routing.SUB_ROUTE_PARTNERS.GROUP_MODAL.LOADING' | t }}</span>
        </div>
      } @else if (partners().length === 0) {
        <div class="flex flex-col items-center justify-center py-12 text-gray-500">
          <mat-icon class="mb-2 text-4xl">person_off</mat-icon>
          <span>{{ 'routing.SUB_ROUTE_PARTNERS.GROUP_MODAL.EMPTY' | t }}</span>
        </div>
      } @else {
        <div class="partners-list">
          @for (partner of partners(); track partner._id) {
            <app-partner-card
              [partner]="partner"
              [companyId]="data.companyId ?? null"
              [selected]="isSelected(partner._id)"
              [disableIndividualCalendar]="selectedIds().size > 1"
              (selectionChange)="onSelectionChange(partner._id, $event)"
              (openCalendar)="openCalendarModal(partner)"
            />
          }
        </div>
      }
    </mat-dialog-content>

    <!-- Footer with Paginator (always visible) -->
    <mat-dialog-actions align="center" class="dialog-footer">
      <mat-paginator
        [length]="totalItems()"
        [pageSize]="pageSize"
        [pageIndex]="currentPage() - 1"
        [pageSizeOptions]="[10, 25, 50]"
        (page)="onPageChange($event)"
      />
    </mat-dialog-actions>
  `,
  styles: `
    .partners-list {
      display: flex;
      flex-direction: column;
      padding: 1rem;
    }

    .count-badge {
      font-size: 0.75rem;
      font-weight: 600;
      padding: 0.125rem 0.5rem;
      border-radius: 9999px;
      background: rgba(var(--fuse-primary-rgb), 0.12);
      color: rgba(var(--fuse-primary-rgb), 1);
    }

    .modal-search {
      padding: 0.75rem 1rem;
      border-bottom: 1px solid rgba(var(--fuse-border-rgb), 0.12);
      background: rgba(var(--fuse-bg-default-rgb), 1);
    }

    .selection-bar {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 0.5rem 1rem;
      background: rgba(var(--fuse-primary-rgb), 0.08);
      border-bottom: 1px solid rgba(var(--fuse-primary-rgb), 0.2);

      span {
        color: rgba(var(--fuse-primary-rgb), 1);
      }
    }

    .dialog-footer {
      border-top: 1px solid rgba(var(--fuse-border-rgb), 0.12);
      padding: 0 !important;
      min-height: auto !important;
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GroupPartnersModalComponent implements OnInit {
  private dialogRef = inject(MatDialogRef<GroupPartnersModalComponent>);
  private dialog = inject(MatDialog);
  private businessPartnerService = inject(BusinessPartnerService);
  private destroyRef = inject(DestroyRef);

  readonly data: GroupPartnersModalData = inject(MAT_DIALOG_DATA);

  readonly loading = signal(true);
  readonly partners = signal<BusinessPartnerBySubRoute[]>([]);
  readonly totalItems = signal(0);
  readonly currentPage = signal(1);
  readonly pageSize = 10;
  readonly searchTerm = signal('');
  readonly hasChanges = signal(false);

  // Debounced search subject
  private readonly search$ = new Subject<string>();

  // === SELECTION STATE ===
  readonly selectedIds = signal<Set<string>>(new Set());
  readonly selectedPartners = computed(() =>
    this.partners().filter((p) => this.selectedIds().has(p._id))
  );

  ngOnInit(): void {
    this.setupSearchDebounce();
    this.loadPartners();
  }

  private setupSearchDebounce(): void {
    this.search$
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.currentPage.set(1);
        this.loadPartners();
      });
  }

  onClose(): void {
    this.dialogRef.close(this.hasChanges());
  }

  onPageChange(event: PageEvent): void {
    this.currentPage.set(event.pageIndex + 1);
    this.loadPartners();
  }

  onSearchChange(term: string): void {
    this.searchTerm.set(term); // Update UI immediately
    this.search$.next(term); // Debounce API call
  }

  // Selection methods
  isSelected(id: string): boolean {
    return this.selectedIds().has(id);
  }

  onSelectionChange(id: string, checked: boolean): void {
    this.selectedIds.update((set) => {
      const newSet = new Set(set);
      if (checked) {
        newSet.add(id);
      } else {
        newSet.delete(id);
      }
      return newSet;
    });
  }

  clearSelection(): void {
    this.selectedIds.set(new Set());
  }

  /**
   * Open calendar modal for assigning frequency to a single partner.
   */
  openCalendarModal(partner: BusinessPartnerBySubRoute): void {
    const calendarDialogRef = this.dialog.open(CalendarPeriodicityComponent, {
      data: {
        subRouteId: this.data.subRouteId,
        businessPartners: [partner],
        frecuencySelected: null,
      },
      disableClose: true,
    });

    calendarDialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.hasChanges.set(true);
        this.loadPartners();
        this.clearSelection();
      }
    });
  }

  /**
   * Open calendar modal for assigning frequency to all selected partners.
   */
  openBulkCalendarModal(): void {
    const partners = this.selectedPartners();
    if (partners.length === 0) return;

    const calendarDialogRef = this.dialog.open(CalendarPeriodicityComponent, {
      data: {
        subRouteId: this.data.subRouteId,
        businessPartners: partners,
        frecuencySelected: null,
      },
      disableClose: true,
    });

    calendarDialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.hasChanges.set(true);
        this.loadPartners();
        this.clearSelection();
      }
    });
  }

  private loadPartners(): void {
    this.loading.set(true);

    const filters = this.buildFiltersForGroup();
    const search = this.searchTerm().trim();

    this.businessPartnerService
      .findUnassignedToSubRouteBusinessPartnerWithCount(
        filters,
        {
          currentPage: this.currentPage(),
          limitPerPage: this.pageSize,
          ...(search && { search }),
        },
        this.data.subRouteId,
        this.data.companyId ?? null
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.partners.set(response.data?.data ?? []);
          // pageInfo is an array: [{ _id: null, total: X }]
          this.totalItems.set(response.data?.pageInfo?.[0]?.total ?? 0);
          this.loading.set(false);
        },
        error: () => {
          this.partners.set([]);
          this.totalItems.set(0);
          this.loading.set(false);
        },
      });
  }

  private buildFiltersForGroup(): AssignedPartnersSubRouteFilters {
    const criteria = this.data.groupingCriteria;
    const item = this.data.group;
    const filters: AssignedPartnersSubRouteFilters = {};

    switch (criteria) {
      case BPGroupingCriteria.BUSINESS_PARTNER_GROUP:
        filters.businessPartnerGroupId = item.id;
        break;
      case BPGroupingCriteria.BUSINESS_PARTNER_SUB_GROUP:
        filters.businessPartnerSubGroupId = item.id;
        break;
      case BPGroupingCriteria.ROUTE:
        filters.routeId = item.id;
        break;
      case BPGroupingCriteria.ROUTE_AREA:
        filters.routeAreaId = item.id;
        break;
      case BPGroupingCriteria.SALES_OFFICE:
        filters.salesOfficeId = item.id;
        break;
      case BPGroupingCriteria.LOCATION_ONE:
        filters.locationOneCode = item.code === 'S/N' ? null : item.code;
        break;
      case BPGroupingCriteria.LOCATION_TWO:
        filters.locationTwoCode = item.code === 'S/N' ? null : item.code;
        break;
      case BPGroupingCriteria.LOCATION_THREE:
        filters.locationThreeCode = item.code === 'S/N' ? null : item.code;
        break;
      case BPGroupingCriteria.LOCATION_FOUR:
        filters.locationFourCode = item.code === 'S/N' ? null : item.code;
        break;
    }

    return filters;
  }
}
